import { useEffect, useState, useCallback } from "react";
import api, { MAD, apiError } from "../lib/api";
import { Modal, Field } from "./Modal";
import { Landmark, Coins, Bike, Calculator, Settings2, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const fmtD = (s) => (s ? new Date(s).toLocaleDateString("fr-FR") : "—");

function Line({ label, value, accent, testid, strong, sub }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-2" style={{ borderBottom: "1px solid var(--tm-border)" }} data-testid={testid}>
      <span className={`text-sm ${strong ? "font-semibold" : "text-[var(--tm-muted)]"}`}>{label}{sub && <span className="block text-[11px] text-[var(--tm-muted)]">{sub}</span>}</span>
      <span className={`tabular whitespace-nowrap ${strong ? "font-display text-2xl" : "font-semibold"}`} style={{ color: accent || "var(--tm-beige)" }}>{value}</span>
    </div>
  );
}

function ComptageModal({ open, onClose, theorique, onSaved }) {
  const [f, setF] = useState({ date: today(), montant_compte: "", commentaire: "" });
  const ecart = f.montant_compte === "" ? null : Math.round((Number(f.montant_compte) - theorique) * 100) / 100;
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/tresorerie/comptages", { ...f, montant_compte: Number(f.montant_compte) });
      toast.success(`Comptage enregistré — écart ${MAD(data.ecart)}`); onSaved(); onClose();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Comptage de caisse" testid="comptage-modal">
      <form onSubmit={save}>
        <Field label="Date du comptage"><input type="date" className="tm-input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="comptage-date" /></Field>
        <Field label="Montant réellement compté (DH)"><input type="number" step="0.01" min="0" required className="tm-input" value={f.montant_compte} onChange={(e) => setF({ ...f, montant_compte: e.target.value })} data-testid="comptage-montant" /></Field>
        <div className="tm-card p-3 mb-3 grid grid-cols-2 gap-3" style={{ background: "var(--tm-bg)" }}>
          <div><span className="text-[10px] uppercase text-[var(--tm-muted)]">Théorique (fin de période)</span><p className="tabular font-semibold" data-testid="comptage-theorique">{MAD(theorique)}</p></div>
          <div><span className="text-[10px] uppercase text-[var(--tm-muted)]">Écart</span><p className="tabular font-semibold" style={{ color: ecart == null ? "var(--tm-muted)" : ecart >= 0 ? "#4CAF50" : "#F44336" }} data-testid="comptage-ecart">{ecart == null ? "—" : MAD(ecart)}</p></div>
        </div>
        <p className="text-[11px] text-[var(--tm-muted)] mb-3">L'écart définitif est recalculé par le serveur sur le théorique à la date du comptage.</p>
        <Field label="Commentaire"><input className="tm-input" value={f.commentaire} onChange={(e) => setF({ ...f, commentaire: e.target.value })} data-testid="comptage-commentaire" /></Field>
        <button type="submit" className="tm-btn w-full" data-testid="comptage-save">Enregistrer le comptage</button>
      </form>
    </Modal>
  );
}

function ParamsModal({ open, onClose, onSaved }) {
  const [f, setF] = useState(null);
  useEffect(() => { if (open) api.get("/tresorerie/parametres").then(({ data }) => setF({ ...data, glovo_cout_pct: data.glovo_cout_pct ?? "" })); }, [open]);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/tresorerie/parametres", { ...f, solde_caisse: Number(f.solde_caisse) || 0, solde_banque: Number(f.solde_banque) || 0, glovo_cout_pct: f.glovo_cout_pct === "" ? null : Number(f.glovo_cout_pct) });
      toast.success("Paramètres enregistrés"); onSaved(); onClose();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Paramètres de trésorerie" testid="treso-params-modal">
      {f && (
        <form onSubmit={save}>
          <Field label="Date des soldes de départ"><input type="date" className="tm-input" value={f.date_depart || ""} onChange={(e) => setF({ ...f, date_depart: e.target.value })} data-testid="treso-date-depart" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Solde caisse espèces (DH)"><input type="number" step="0.01" className="tm-input" value={f.solde_caisse} onChange={(e) => setF({ ...f, solde_caisse: e.target.value })} data-testid="treso-solde-caisse" /></Field>
            <Field label="Solde compte bancaire (DH)"><input type="number" step="0.01" className="tm-input" value={f.solde_banque} onChange={(e) => setF({ ...f, solde_banque: e.target.value })} data-testid="treso-solde-banque" /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={!!f.soldes_renseignes} onChange={(e) => setF({ ...f, soldes_renseignes: e.target.checked })} data-testid="treso-soldes-renseignes" /> Soldes de départ vérifiés (sinon la carte n'affiche que les mouvements du mois)</label>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Coût matière Glovo">
              <select className="tm-input" value={f.glovo_cout_mode} onChange={(e) => setF({ ...f, glovo_cout_mode: e.target.value })} data-testid="treso-glovo-mode">
                <option value="auto">Taux de la Boutique (auto)</option><option value="manuel">Taux manuel</option>
              </select>
            </Field>
            {f.glovo_cout_mode === "manuel" && <Field label="Taux coût matière Glovo (%)"><input type="number" step="0.1" className="tm-input" value={f.glovo_cout_pct} onChange={(e) => setF({ ...f, glovo_cout_pct: e.target.value })} data-testid="treso-glovo-pct" /></Field>}
          </div>
          <button type="submit" className="tm-btn w-full" data-testid="treso-params-save">Enregistrer</button>
        </form>
      )}
    </Modal>
  );
}

function MouvementModal({ open, onClose, onSaved }) {
  const [f, setF] = useState({ type: "depot_banque", montant: "", date: today(), compte: "caisse", commentaire: "" });
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/tresorerie/mouvements", { ...f, montant: Number(f.montant) }); toast.success("Mouvement enregistré"); onSaved(); onClose(); }
    catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };
  const gerant = f.type.endsWith("gerant");
  return (
    <Modal open={open} onClose={onClose} title="Mouvement interne" testid="mouvement-modal">
      <form onSubmit={save}>
        <Field label="Type">
          <select className="tm-input" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="mouvement-type">
            <option value="depot_banque">Dépôt d'espèces à la banque</option><option value="retrait_banque">Retrait banque → caisse</option>
            <option value="apport_gerant">Apport du gérant</option><option value="retrait_gerant">Retrait du gérant</option>
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Montant (DH)"><input type="number" step="0.01" min="0" required className="tm-input" value={f.montant} onChange={(e) => setF({ ...f, montant: e.target.value })} data-testid="mouvement-montant" /></Field>
          <Field label="Date"><input type="date" className="tm-input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="mouvement-date" /></Field>
        </div>
        {gerant && <Field label="Compte concerné"><select className="tm-input" value={f.compte} onChange={(e) => setF({ ...f, compte: e.target.value })} data-testid="mouvement-compte"><option value="caisse">Caisse espèces</option><option value="banque">Banque</option></select></Field>}
        <Field label="Commentaire"><input className="tm-input" value={f.commentaire} onChange={(e) => setF({ ...f, commentaire: e.target.value })} /></Field>
        <p className="text-[11px] text-[var(--tm-muted)] mb-3">Ni une vente ni une dépense : seuls les soldes caisse / banque bougent.</p>
        <button type="submit" className="tm-btn w-full" data-testid="mouvement-save">Enregistrer</button>
      </form>
    </Modal>
  );
}

function Row({ n, label, sub, value, testid, accent }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 px-1" style={{ borderBottom: "1px solid var(--tm-border)" }} data-testid={testid}>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-[0.14em] font-semibold"><span className="text-[var(--tm-muted)] mr-2">{n}</span>{label}</p>
        {sub && <p className="text-[11px] text-[var(--tm-muted)] mt-0.5">{sub}</p>}
      </div>
      <span className="tabular font-semibold text-lg whitespace-nowrap" style={{ color: accent || "var(--tm-beige)" }} data-testid={`${testid}-value`}>{MAD(value)}</span>
    </div>
  );
}

function TresoTable({ t }) {
  const g = t.glovo;
  const fp = t.flux_periode;
  const recSub = [g.a_recevoir_releves ? `${MAD(g.a_recevoir_releves)} de relevés non encore reçus` : "",
    g.a_recevoir_estime ? `dont estimé (jours sans relevé) : ${MAD(g.a_recevoir_estime)}` : ""].filter(Boolean).join(" · ") || "aucun virement attendu";
  return (
    <div data-testid="treso-table">
      <Row n="1" label="Caisse" value={t.especes_caisse} testid="treso-caisse"
        sub={`Solde calculé des espèces · période : +${MAD(fp.ventes_especes + fp.glovo_especes + fp.b2b_encaisse)} entrées · −${MAD(fp.depenses_especes)} dépenses`} />
      <Row n="2" label="Banque" value={t.banque} testid="treso-banque"
        sub={`Solde calculé des opérations · période : +${MAD(fp.ventes_carte)} carte · +${MAD(fp.glovo_virements_recus)} virements Glovo · −${MAD(fp.depenses_banque)} dépenses`} />
      <Row n="3" label="À recevoir de Glovo" value={g.a_recevoir} testid="treso-glovo-recevoir" accent="#FFC244" sub={recSub} />
      <div className="flex items-center justify-between gap-4 mt-3 p-4 rounded-md" style={{ background: "rgba(212,175,55,.10)", border: "1px dashed rgba(212,175,55,.7)" }} data-testid="treso-globale">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.14em] font-semibold" style={{ color: "var(--tm-gold)" }}><span className="mr-2 opacity-70">4</span>Trésorerie globale prévisionnelle</p>
          <p className="text-[11px] text-[var(--tm-muted)] mt-0.5" data-testid="treso-globale-note">
            Caisse + Banque + À recevoir de Glovo · <strong>prévision, pas des liquidités</strong> : réellement disponible aujourd'hui {MAD(t.liquidites_disponibles)}
          </p>
        </div>
        <span className="tabular font-display text-2xl whitespace-nowrap" style={{ color: "var(--tm-gold)" }} data-testid="treso-globale-value">{MAD(t.tresorerie_globale_previsionnelle)}</span>
      </div>
    </div>
  );
}

function TresoDetails({ t }) {
  const g = t.glovo;
  const dc = t.dernier_comptage;
  return (
    <details className="mt-4" data-testid="treso-details">
      <summary className="text-xs uppercase tracking-[0.14em] cursor-pointer text-[var(--tm-muted)]" data-testid="treso-details-toggle">Détails</summary>
      <div className="mt-2">
        <Line label="Glovo déjà encaissé en espèces (période)" value={MAD(g.encaisse_especes_periode)} accent="#FFC244" testid="treso-glovo-especes" />
        <Line label="Virements Glovo à recevoir" value={MAD(g.a_recevoir)} testid="treso-glovo-detail"
          sub={g.a_recevoir_detail.map((x) => `${x.periode} : ${MAD(x.montant)} ${x.date_prevue ? "le " + fmtD(x.date_prevue) : "(" + x.statut + ")"}`).join(" · ") || "aucun"} />
        <Line label="Charges fixes restant à payer ce mois" value={`− ${MAD(t.charges_fixes_restant)}`} accent="#FF9800" testid="treso-charges-restant" />
        <Line label="Trésorerie après charges (prévisionnelle)" value={MAD(t.tresorerie_apres_charges)} accent={t.tresorerie_apres_charges >= 0 ? "#4CAF50" : "#F44336"} testid="treso-apres-charges" />
        {dc && <p className="text-xs text-[var(--tm-muted)] mt-2" data-testid="treso-dernier-comptage"><Coins size={12} className="inline mr-1" />Dernier comptage {fmtD(dc.date)} : {MAD(dc.montant_compte)} (écart {MAD(dc.ecart)})</p>}
      </div>
    </details>
  );
}

export function TresorerieCard({ start, end }) {
  const [t, setT] = useState(null);
  const [modal, setModal] = useState("");
  const load = useCallback(() => {
    api.get(`/tresorerie?date_from=${start}&date_to=${end}`).then(({ data }) => setT(data)).catch(() => setT(null));
  }, [start, end]);
  useEffect(() => { load(); }, [load]);
  if (!t) return null;
  return (
    <div className="tm-card p-5 mb-6" style={{ borderColor: "rgba(212,175,55,.45)" }} data-testid="tresorerie-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Landmark size={18} style={{ color: "var(--tm-gold)" }} />
          <h2 className="font-display text-xl">Trésorerie</h2>
          <span className="text-xs text-[var(--tm-muted)]">au {fmtD(t.periode.date_to)}{t.solde_depart.renseignes ? ` · soldes de départ au ${fmtD(t.solde_depart.date)}` : ""}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="tm-btn" onClick={() => setModal("comptage")} data-testid="comptage-caisse-btn"><Calculator size={15} /> Comptage de caisse</button>
          <button className="tm-btn tm-btn-ghost" onClick={() => setModal("mouvement")} data-testid="mouvement-interne-btn"><ArrowLeftRight size={15} /> Mouvement</button>
          <button className="tm-btn tm-btn-ghost" onClick={() => setModal("params")} data-testid="treso-params-btn"><Settings2 size={15} /> Soldes de départ</button>
        </div>
      </div>
      {!t.solde_depart.renseignes && (
        <div className="tm-card p-3 mb-3 text-sm flex gap-2" style={{ borderColor: "#FF980066", background: "rgba(255,152,0,.08)" }} data-testid="treso-soldes-non-renseignes">
          <span style={{ color: "#FF9800" }}>⚠</span><span>{t.avertissement}</span>
        </div>
      )}
      <TresoTable t={t} />
      <TresoDetails t={t} />
      <p className="text-[11px] text-[var(--tm-muted)] mt-3"><Bike size={11} className="inline mr-1" />{t.note}</p>
      <ComptageModal open={modal === "comptage"} onClose={() => setModal("")} theorique={t.especes_caisse} onSaved={load} />
      <ParamsModal open={modal === "params"} onClose={() => setModal("")} onSaved={load} />
      <MouvementModal open={modal === "mouvement"} onClose={() => setModal("")} onSaved={load} />
    </div>
  );
}
